import { RecipeInputField, RecipeStep, SDBaseModel } from "@/api/sdk"
import { cn } from "@/lib/utils"
import classNames from "classnames"
import { motion } from "framer-motion"
import { FC, Fragment, useCallback, useMemo, useState, useRef } from "react"
import { useDrag, useDrop } from "react-dnd"
import { Controller, FieldArrayWithId, UseFormReturn, useFieldArray, useWatch } from "react-hook-form"
import { GraphField } from "../../../ComfyUI/utils/graph"
import Input from "../../../Input"
import Selector from "../../../Selector"
import Toggle from "../../../Toggle"
import { ChevronDownIcon, MenuIcon, PlusIcon, Target3Icon, XIcon, UploadIcon } from "../../../shared/icons"
import { workflowInputTypes } from "./AddInputPopover"
import client from "@/api/client"
import { INPUT_IMAGE_UPLOAD_SIZE_LIMIT } from "@/constants/file"
import { useToast } from "@/hooks"
import { useAuth } from "@/providers/authContext"
import { bytesToSize } from "@/utils/bytes-to-size"
import { handleConvertHEICtoJPG } from "@/utils/convert"
import generateCaptchaToken from "@/utils/generateCaptchaToken"
import { resizeImage } from "@/utils/resize-image"

export type RecipeInputFieldName = "inputs" | "advancedInputs"

type RecipeStepForm = Omit<RecipeStep, "inputs"> & {
  inputs: Array<RecipeInputField & { isNew?: boolean }>
  advancedInputs: Array<RecipeInputField & { isNew?: boolean }>
}

export type RecipeForm = UseFormReturn<RecipeStepForm, any, undefined>

export type WorkflowInput = {
  id: string
  type: string
  field?: string
  name?: string
  description?: string
  tooltip?: string
}

export type DropItem = {
  index: number
  fieldPrefix: RecipeInputFieldName
}

export type ComfyUIWorkflowSettingProps = {
  graphInputs: GraphField[]
  form: RecipeForm
  index: number
  fieldPrefix: RecipeInputFieldName
  onSelect: (comfyKey: string) => void
  onRemove: () => void
  onSwap: (indexA: DropItem, indexB: DropItem) => void
  disabled?: boolean
  field: FieldArrayWithId<RecipeStep, "inputs", "id">
}

const shorten = (string: string, maxLength: number) => {
  return string.length <= maxLength ? string : string.slice(0, maxLength - 2) + "..."
}

const baseModels = [
  { id: SDBaseModel.All, name: "Any" },
  { id: SDBaseModel.Sd1X, name: "SD1.5 only" },
  { id: SDBaseModel.SdXl, name: "SDXL only" },
  { id: SDBaseModel.Flux1, name: "FLUX only" },
]

const ComfyUIWorkflowSetting: FC<ComfyUIWorkflowSettingProps> = props => {
  const { graphInputs, form, index, disabled, onSelect, onRemove, onSwap, field, fieldPrefix } = props

  const { control } = form
  const {
    fields: options,
    append,
    remove,
  } = useFieldArray({
    control,
    name: `${fieldPrefix}.${index}.options`,
    rules: {
      validate: (v, f) => {
        if (["select", "gallery-select", "radio"].includes(f[fieldPrefix][index]?.type)) {
          return v.length > 0 ? true : "This field is required"
        }

        return true
      },
    },
  })

  const values = useWatch({
    control,
    name: `${fieldPrefix}.${index}`,
  })

  const type = values?.["type"]
  const comfyKey = values?.["comfyKey"]
  const [open, _setOpen] = useState(!!values.isNew)
  const setOpen = (open: boolean) => {
    _setOpen(open)
    form.setValue(`${fieldPrefix}.${index}.isNew`, false)
  }

  // State for thumbnail uploads
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const [currentUploadOptionIndex, setCurrentUploadOptionIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const { user } = useAuth();

  const handleThumbnailUpload = async (file: Blob, optionIndex: number) => {
    if (!file) return;

    if (file.size > INPUT_IMAGE_UPLOAD_SIZE_LIMIT) {
      toast({
        status: "error",
        title: "File size too large",
        message: [
          `Input image exceeds ${bytesToSize(INPUT_IMAGE_UPLOAD_SIZE_LIMIT)}, please scale down the image and try again`,
        ],
      });
      return;
    }

    setUploadingIndex(optionIndex);
    let imageUrl: string | undefined = undefined;

    try {
      let processedFile = file;
      if (processedFile.type === "image/heic" || processedFile.name.endsWith(".heic")) {
        processedFile = await handleConvertHEICtoJPG(processedFile);
      }
      // Resize to a smaller size appropriate for thumbnails, e.g., 512px
      processedFile = await resizeImage(processedFile, 400);

      if (user) {
        // Use authenticated upload
        const res = await client.api.recipeTaskControllerUpload({ files: [processedFile as File] });
        imageUrl = res.data.images[0]?.url;
      } else {
        // Use unauthenticated upload
        const captchaToken = await generateCaptchaToken();
        const res = await client.api.recipeTaskControllerUploadImagesForFreeTask({ files: [processedFile as File], captchaToken });
        imageUrl = res.data.images[0]?.url;
      }

      if (imageUrl) {
        form.setValue(`${fieldPrefix}.${index}.options.${optionIndex}.thumbnail`, imageUrl);
        toast({ status: "success", title: "Thumbnail uploaded" });
      } else {
        throw new Error("Upload failed, no image URL returned.");
      }
    } catch (err: any) {
      console.error("Thumbnail upload failed:", err);
      toast({ status: "error", title: "Upload failed", message: err.message || "Could not upload thumbnail." });
      // Handle specific errors like auth errors if needed
    } finally {
      setUploadingIndex(null);
      // Reset file input value to allow re-uploading the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const hasError = !!form.formState.errors[fieldPrefix]?.[index]

  const availableInputs = useMemo(() => {
    const dataTypes = workflowInputTypes.find(i => i.type === type)?.dataTypes ?? [type]
    return graphInputs
      .filter(i => dataTypes.includes(i.type))
      .map(i => ({ ...i, name: `#${i.nodeId}.${i.field} (${shorten(i.nodeName, 25)})` }))
  }, [type, graphInputs])

  const availableWeightInputs = useMemo(() => {
    return graphInputs
      .filter(i => i.type === "number")
      .filter(i => !comfyKey || i.id.startsWith(comfyKey.split(".")[0]))
      .map(i => ({ ...i, name: `#${i.nodeId}.${i.field} (${shorten(i.nodeName, 25)})` }))
  }, [comfyKey, graphInputs])

  const [{ isDragging }, dragRef] = useDrag(
    () => ({
      type: "comfyUiWorkflowInput",
      item: () => ({ fieldPrefix, index }),
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
      end: (item, monitor) => {
        const result = monitor.getDropResult()
        console.log("droping", item, "result", result)
      },
      canDrag() {
        return !disabled
      },
    }),
    [index, disabled],
  )

  const [{ isOver }, dropRef] = useDrop(
    () => ({
      accept: "comfyUiWorkflowInput",
      drop: (item: DropItem) => {
        if (item.index !== index || item.fieldPrefix !== fieldPrefix) {
          onSwap(item, { index, fieldPrefix })
        }
      },
      collect: monitor => ({
        isOver: !!monitor.isOver(),
      }),
    }),
    [index],
  )
  return (
    <div
      ref={ref => {
        dropRef(ref)
      }}
      className="bg-atherGray-900 text-atherGray-300 max-w-[36rem] overflow-hidden"
    >
      <div
        ref={ref => {
          dragRef(ref)
        }}
        className={classNames("border-atherGray-800 py-4", {
          "border-t": index > 0,
          "opacity-50": isDragging,
          "border-t-4": isOver,
        })}
      >
        <div className="flex gap-2 items-center overflow-hidden">
          <MenuIcon className="text-atherGray-500 cursor-ns-resize" />
          <a
            className={classNames(
              "flex-1 flex items-center justify-start overflow-hidden gap-2 cursor-pointer text-atherGray-300",
              {
                "text-red-500": hasError,
              },
            )}
            onClick={() => setOpen(!open)}
          >
            <p className="text-xs font-semibold truncate">{values["name"] || field.name || type}</p>
            <ChevronDownIcon
              className={classNames("w-3 h-3", {
                "-rotate-90": !open,
              })}
            />
          </a>
          <Target3Icon
            onClick={() => onSelect(values["comfyKey"] || field.comfyKey)}
            className={classNames({
              "cursor-pointer": !!values["comfyKey"] || !!field.comfyKey,
              "opacity-60": !values["comfyKey"] && !field.comfyKey,
            })}
            width={12}
            height={12}
          />
          <XIcon
            width={12}
            height={12}
            className={cn("text-red-500", { "cursor-pointer": !disabled, "opacity-70": disabled })}
            onClick={() => !disabled && onRemove()}
          />
        </div>
        <motion.div
          initial={{ height: "auto", opacity: 1 }}
          animate={
            open
              ? { height: "auto", opacity: 1, display: "block" }
              : { height: 0, opacity: 0, transitionEnd: { display: "none" } }
          }
          className={classNames({
            "overflow-hidden": !open,
          })}
        >
          <div className="flex flex-col gap-1.5 pt-2">
            <div>
              <Controller
                name={`${fieldPrefix}.${index}.comfyKey`}
                control={control}
                rules={{ required: { value: true, message: "This field is required" } }}
                render={({ field, fieldState }) => (
                  <>
                    <Selector
                      searchable
                      placement="bottom"
                      className="text-xs h-7 pl-2 py-1"
                      listClassName="bg-[#464646] text-sm"
                      value={availableInputs.find(i => i.id === field.value)?.name ?? "Select mapping input"}
                      options={availableInputs}
                      disabled={disabled}
                      onSelect={v => {
                        field.onChange(v.id)

                        onSelect(v.id)
                        if (type === "select" || type === "gallery-select" || type === "radio") {
                          form.setValue(
                            `${fieldPrefix}.${index}.options`,
                            (v.values ?? [v.value]).map(value => ({ display: value, value })),
                          )
                        } else if (type === "model" || type === "lora" || type === "lora-simple") {
                          form.setValue(`${fieldPrefix}.${index}.baseModel`, SDBaseModel.Sd1X)
                        } else if (type === "prompt") {
                          form.setValue(`${fieldPrefix}.${index}.allowWildcard`, true)
                        }
                      }}
                    />
                    {fieldState.error && (
                      <p className="text-red-500 text-xs error-message">{fieldState.error.message}</p>
                    )}
                  </>
                )}
              ></Controller>
            </div>
            {type === "lora-simple" && (
              <Controller
                name={`${fieldPrefix}.${index}.weightComfyKey`}
                control={control}
                rules={{ required: { value: true, message: "This field is required" } }}
                render={({ field, fieldState }) => (
                  <>
                    <Selector
                      searchable
                      placement="bottom"
                      className="text-xs h-7 pl-2 py-1"
                      listClassName="bg-[#464646] text-sm"
                      value={
                        availableWeightInputs.find(i => i.id === field.value)?.name ?? "Select weight mapping input"
                      }
                      options={availableWeightInputs}
                      disabled={disabled || !comfyKey}
                      onSelect={v => {
                        field.onChange(v.id)
                        onSelect(v.id)
                      }}
                    />
                    {fieldState.error && (
                      <p className="text-red-500 text-xs error-message">{fieldState.error.message}</p>
                    )}
                  </>
                )}
              ></Controller>
            )}
            <Controller
              name={`${fieldPrefix}.${index}.name`}
              control={control}
              rules={{ required: { value: true, message: "This field is required" } }}
              render={({ field, fieldState }) => (
                <>
                  <Input
                    containerClassName="flex-1"
                    className="rounded-lg text-xs h-7 px-2 py-1"
                    placeholder="Name"
                    disabled={disabled}
                    {...field}
                  />
                  {fieldState.error && <p className="text-red-500 text-xs error-message">{fieldState.error.message}</p>}
                </>
              )}
            ></Controller>

            <Controller
              name={`${fieldPrefix}.${index}.description`}
              control={control}
              render={({ field, fieldState }) => (
                <div className="relative overflow-hidden rounded-lg p-1 bg-atherGray-800">
                  <textarea
                    disabled={disabled}
                    className="bg-transparent w-full px-1 text-xs disabled:text-atherGray-400 disabled:placeholder:text-gray-500"
                    placeholder="Decscription"
                    rows={2}
                    {...field}
                  />
                </div>
              )}
            ></Controller>

            {(type === "text" || type === "prompt") && (
              <>
                <Input
                  className="rounded-lg text-xs h-7 px-2 py-1"
                  placeholder="Placeholder"
                  disabled={disabled}
                  {...form.register(`${fieldPrefix}.${index}.placeholder`)}
                />
              </>
            )}
            {type === "number" && (
              <div className="flex flex-col space-y-2">
                <Controller
                  name={`${fieldPrefix}.${index}.min`}
                  control={control}
                  rules={{ required: { value: true, message: "This field is required" } }}
                  render={({ field, fieldState }) => (
                    <div>
                      <div className="flex items-center">
                        <p className="text-xs mr-2 min-w-[2rem]">Min:</p>
                        <Input
                          className="max-w-[10rem] rounded-lg text-xs flex-1 h-7 px-2 py-1"
                          type="number"
                          disabled={disabled}
                          placeholder="0"
                          {...field}
                          value={field.value ? parseFloat(parseFloat(field.value.toString()).toFixed(3)) : undefined}
                          onChange={e => {
                            field.onChange(parseFloat(parseFloat(e.target.value).toFixed(3)))
                          }}
                        />
                      </div>
                      {fieldState.error && (
                        <p className="text-red-500 text-xs error-message mt-1">{fieldState.error.message}</p>
                      )}
                    </div>
                  )}
                />
                <Controller
                  name={`${fieldPrefix}.${index}.max`}
                  control={control}
                  rules={{
                    required: { value: true, message: "This field is required" },
                    validate: {
                      //check max bigger than min
                      validate: (_, values) => {
                        return parseFloat(values[fieldPrefix][index].max?.toString() ?? "0") >=
                          parseFloat(values[fieldPrefix][index].min?.toString() ?? "0")
                          ? true
                          : "Max must be bigger than min"
                      },
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <div>
                      <div className="flex items-center">
                        <p className="text-xs mr-2 min-w-[2rem]">Max:</p>
                        <Input
                          disabled={disabled}
                          className="max-w-[10rem] rounded-lg text-xs flex-1 h-7 px-2 py-1"
                          type="number"
                          placeholder="0"
                          {...field}
                          value={field.value ? parseFloat(parseFloat(field.value.toString()).toFixed(3)) : undefined}
                          onChange={e => {
                            field.onChange(parseFloat(parseFloat(e.target.value).toFixed(3)))
                          }}
                        />
                      </div>

                      {fieldState.error && (
                        <p className="text-red-500 text-xs error-message mt-1">{fieldState.error.message}</p>
                      )}
                    </div>
                  )}
                />
                <Controller
                  name={`${fieldPrefix}.${index}.step`}
                  control={control}
                  rules={{
                    required: { value: true, message: "This field is required" },
                    validate: {
                      //check step smaller than max - min
                      validate: (_, values) =>
                        parseFloat(values[fieldPrefix][index].step?.toString() ?? "0") <=
                        parseFloat(values[fieldPrefix][index].max?.toString() ?? "0") -
                          parseFloat(values[fieldPrefix][index].min?.toString() ?? "0")
                          ? true
                          : "Step must be smaller than max - min",
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <div>
                      <div className="flex items-center">
                        <p className="text-xs mr-2 min-w-[2rem]">Step:</p>
                        <Input
                          disabled={disabled}
                          className="max-w-[10rem] rounded-lg text-xs flex-1 h-7 px-2 py-1"
                          type="number"
                          placeholder="0"
                          {...field}
                          value={field.value ? parseFloat(parseFloat(field.value.toString()).toFixed(3)) : undefined}
                          onChange={e => {
                            field.onChange(parseFloat(parseFloat(e.target.value).toFixed(3)))
                          }}
                        />
                      </div>

                      {fieldState.error && (
                        <p className="text-red-500 text-xs error-message mt-1">{fieldState.error.message}</p>
                      )}
                    </div>
                  )}
                />
              </div>
            )}
            {(type === "select" || type === "gallery-select" || type === "radio") && (
              <div>
                <div
                  className="flex gap-2 items-center cursor-pointer"
                  onClick={() => append({ value: "", display: "" })}
                >
                  <p className="text-xs font-semibold">Values</p>
                  <PlusIcon height={10} width={10} />
                </div>
                <div className="flex flex-col gap-y-2 mt-1">
                  {options.map((option, idx) => (
                    <Fragment key={option.id}>
                      {type === "gallery-select" ? (
                        <div className="grid grid-cols-[auto_1fr_auto] gap-x-2 items-center">
                          <Controller
                            name={`${fieldPrefix}.${index}.options.${idx}.thumbnail`}
                            control={control}
                            render={({ field }) => {
                              const thumbnailUrl = typeof field.value === 'string' && field.value ? field.value : undefined;
                              const isLoading = uploadingIndex === idx;

                              return (
                                <div
                                  className={cn(
                                    "relative bg-atherGray-700 border border-dashed border-atherGray-600 rounded w-16 h-16 flex items-center justify-center text-xs text-atherGray-400 cursor-pointer hover:border-atherGray-500 hover:text-atherGray-300",
                                    { "opacity-50 pointer-events-none": isLoading }
                                  )}
                                  onClick={() => {
                                    if (!isLoading) {
                                      setCurrentUploadOptionIndex(idx);
                                      fileInputRef.current?.click();
                                    }
                                  }}
                                >
                                  {/* Loading Spinner Overlay */}
                                  {isLoading && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded">
                                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                    </div>
                                  )}
                                  {/* Icon or Image */}
                                  {!isLoading && !thumbnailUrl && <UploadIcon className="w-5 h-5" />}
                                  {!isLoading && thumbnailUrl && (
                                    <img src={thumbnailUrl} alt="thumbnail preview" className="absolute inset-0 w-full h-full object-cover rounded" />
                                  )}
                                </div>
                              );
                            }}
                          />
                          <div className="flex flex-col gap-2">
                            <Controller
                              name={`${fieldPrefix}.${index}.options.${idx}.display`}
                              control={control}
                              render={({ field }) => (
                                <Input
                                  disabled={disabled}
                                  className="rounded-lg text-xs h-7 px-2 py-1"
                                  placeholder="Display"
                                  {...field}
                                />
                              )}
                            />
                            <Controller
                              name={`${fieldPrefix}.${index}.options.${idx}.value`}
                              control={control}
                              render={({ field }) => (
                                <Input
                                  disabled={disabled}
                                  className="rounded-lg text-xs h-7 px-2 py-1"
                                  placeholder="Value"
                                  {...field}
                                />
                              )}
                            />
                          </div>
                          <div className="flex items-center">
                            <XIcon
                              width={12}
                              height={12}
                              className="text-red-500 cursor-pointer"
                              onClick={() => remove(idx)}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-[minmax(0,_1fr)_minmax(0,_1fr)_auto] gap-x-1">
                          <Controller
                            name={`${fieldPrefix}.${index}.options.${idx}.display`}
                            control={control}
                            render={({ field }) => (
                              <Input
                                disabled={disabled}
                                className="rounded-l-lg rounded-r-none text-xs h-7 px-2 py-1 border-r border-atherGray-700"
                                placeholder="Display"
                                {...field}
                              />
                            )}
                          />
                          <Controller
                            name={`${fieldPrefix}.${index}.options.${idx}.value`}
                            control={control}
                            render={({ field }) => (
                              <Input
                                disabled={disabled}
                                className="rounded-r-lg rounded-l-none text-xs h-7 px-2 py-1 text-right"
                                placeholder="Value"
                                {...field}
                              />
                            )}
                          />
                          <div className="justify-self-end flex items-center">
                            <XIcon
                              width={12}
                              height={12}
                              className="text-red-500 cursor-pointer"
                              onClick={() => remove(idx)}
                            />
                          </div>
                        </div>
                      )}
                    </Fragment>
                  ))}
                </div>
              </div>
            )}
            {(type === "model" || type === "lora" || type === "lora-simple") && (
              <Controller
                name={`${fieldPrefix}.${index}.baseModel`}
                control={control}
                render={({ field }) => (
                  <Selector
                    placement="bottom"
                    className="text-xs bg-atherGray-800 h-7 pl-2 py-1"
                    listClassName="bg-atherGray-700 text-sm"
                    value={baseModels.find(i => i.id === field.value)?.name ?? "Any"}
                    options={baseModels}
                    onSelect={v => field.onChange(v.id)}
                    disabled={disabled}
                  />
                )}
              ></Controller>
            )}

            <div className="flex gap-2 py-1 flex-col">
              <Controller
                name={`${fieldPrefix}.${index}.optional`}
                control={control}
                render={({ field }) => (
                  <div className="flex-0 flex gap-2 items-center justify-between">
                    <p className="text-xs">Optional</p>
                    <Toggle
                      sizeToggle="sm"
                      disabled={disabled}
                      checked={field.value}
                      onChange={e => {
                        e.stopPropagation()
                        field.onChange(e.target.checked)
                      }}
                    />
                  </div>
                )}
              />
              {type === "image" && (
                <Controller
                  name={`${fieldPrefix}.${index}.private`}
                  control={control}
                  render={({ field }) => (
                    <div className="flex-0 flex gap-2 items-center justify-between">
                      <p className="text-xs">Private</p>
                      <Toggle
                        disabled={disabled}
                        sizeToggle="sm"
                        checked={field.value}
                        onChange={e => {
                          e.stopPropagation()
                          field.onChange(e.target.checked)
                        }}
                      />
                    </div>
                  )}
                />
              )}
            </div>
          </div>
        </motion.div>
      </div>
      {/* Hidden File Input for Thumbnail Upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.heic"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && currentUploadOptionIndex !== null && uploadingIndex === null) {
            handleThumbnailUpload(file, currentUploadOptionIndex);
          } else if (uploadingIndex !== null) {
            console.warn("Cannot start new upload while another is in progress.");
          } else if (currentUploadOptionIndex === null) {
            console.error("File input changed but no option index was set.");
          }
        }}
      />
    </div>
  )
}

export default ComfyUIWorkflowSetting
